import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { Link } from 'react-router-dom';

export default function Home() {
  const { t, i18n } = useTranslation();
  const [heroLoaded, setHeroLoaded] = useState(false);

  useEffect(() => {
    setHeroLoaded(true);
  }, []);

  const getArticleTitle = (article: typeof articles[0]) => {
    switch (i18n.language) {
      case 'fr': return article.titleFr;
      case 'da': return article.titleDa;
      case 'de': return article.titleDe;
      default: return article.titleEn;
    }
  };

  const getArticleExcerpt = (article: typeof articles[0]) => {
    switch (i18n.language) {
      case 'fr': return article.excerptFr;
      case 'da': return article.excerptDa;
      case 'de': return article.excerptDe;
      default: return article.excerptEn;
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden pt-16">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=An%20overhead%20flat%20lay%20of%20neatly%20arranged%20pastel%20streetwear%20clothing%20with%20sneakers%20t-shirts%20hoodies%20and%20caps%20on%20a%20light%20beige%20surface%2C%20soft%20natural%20daylight%2C%20clean%20editorial%20fashion%20photography%2C%20airy%20minimal%20aesthetic%2C%20warm%20neutral%20tones&width=1920&height=1080&seq=hero-main&orientation=landscape&nocache=true"
            alt="Fashion products"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>

        <div className={`relative z-10 text-center px-6 max-w-4xl mx-auto transition-all duration-1000 ${heroLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6" style={{ fontFamily: 'var(--font-heading, serif)' }}>
            {t('hero.title')}
          </h1>
          <p className="text-base md:text-lg text-white/90 leading-relaxed mb-10 max-w-2xl mx-auto">
            {t('hero.subtitle')}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-stone-900 rounded-full font-semibold text-sm hover:bg-stone-100 transition-all hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-external-link-line" />
              {t('hero.ctaAllChinaBuy')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-4 bg-stone-900/80 text-white border border-white/30 rounded-full font-semibold text-sm hover:bg-stone-900 transition-all hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-shopping-bag-line" />
              {t('hero.ctaBuyNow')}
            </a>
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 text-white/80 text-sm hover:text-white transition-colors underline underline-offset-4"
          >
            {t('common.continueToMain')}
            <i className="ri-arrow-right-line" />
          </a>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* Category Section */}
      <section className="bg-stone-50 border-b border-stone-200">
        <div className="w-full px-4 md:px-6 lg:px-10 py-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-900" style={{ fontFamily: 'var(--font-heading, serif)' }}>
              {t('categories.title')}
            </h2>
            <p className="text-sm text-stone-500 mt-2">{t('categories.subtitle')}</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group bg-white rounded-lg border border-stone-200 overflow-hidden hover:border-stone-300 transition-all hover:shadow-md"
              >
                <div className="aspect-square w-full overflow-hidden bg-stone-100">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-2.5 md:p-3 text-center">
                  <span className="text-xs md:text-sm font-medium text-stone-800 leading-tight line-clamp-2">{cat.name}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="w-full px-6 lg:px-10 max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-stone-900 text-center mb-16" style={{ fontFamily: 'var(--font-heading, serif)' }}>
            {t('features.title')}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              t('features.item1'),
              t('features.item2'),
              t('features.item3'),
              t('features.item4'),
            ].map((item, idx) => (
              <div key={idx} className="group text-center p-6 rounded-xl hover:bg-stone-50 transition-colors">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-stone-100 flex items-center justify-center group-hover:bg-stone-200 transition-colors">
                  <i className={`ri-${['file-list-3-line', 'image-line', 'truck-line', 'coupon-line'][idx]} text-xl text-stone-600`} />
                </div>
                <p className="text-sm text-stone-700 leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Section */}
      <section className="py-20 bg-stone-50">
        <div className="w-full px-6 lg:px-10 max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-3" style={{ fontFamily: 'var(--font-heading, serif)' }}>
              {t('articles.title')}
            </h2>
            <p className="text-sm text-stone-500">
              {t('articles.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-white rounded-xl overflow-hidden border border-stone-200 hover:border-stone-300 transition-all hover:shadow-md"
              >
                <div className="h-56 overflow-hidden">
                  <img
                    src={article.image}
                    alt={getArticleTitle(article)}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs font-medium text-stone-500 uppercase tracking-wider">{article.category}</span>
                  <h3 className="text-lg font-bold text-stone-900 mt-2 mb-3 group-hover:text-stone-700 transition-colors" style={{ fontFamily: 'var(--font-heading, serif)' }}>
                    {getArticleTitle(article)}
                  </h3>
                  <p className="text-sm text-stone-500 leading-relaxed line-clamp-3">
                    {getArticleExcerpt(article)}
                  </p>
                  <div className="mt-4 flex items-center gap-1 text-sm font-medium text-stone-700 group-hover:text-stone-900 transition-colors">
                    {t('common.readMore')}
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20cozy%20shopping%20concept%20with%20a%20paper%20shopping%20bag%20filled%20with%20folded%20neutral%20clothing%20and%20a%20small%20gift%20box%20on%20a%20warm%20cream%20table%2C%20soft%20window%20light%2C%20editorial%20lifestyle%20photography%2C%20beige%20tones&width=1920&height=800&seq=cta-bg&orientation=landscape&nocache=true"
            alt="Shopping background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading, serif)' }}>
            Ready to Start Shopping?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            Visit the main AllChinaBuy website to browse the full product catalog, check live prices, and place your order.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-stone-900 rounded-full font-semibold text-sm hover:bg-stone-100 transition-all hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-external-link-line" />
              AllChinaBuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-4 bg-stone-900/80 text-white border border-white/30 rounded-full font-semibold text-sm hover:bg-stone-900 transition-all hover:scale-105 whitespace-nowrap"
            >
              <i className="ri-shopping-bag-line" />
              Buy It Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}